import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import { ArrowForward, Refresh } from '@mui/icons-material';
import MroLabShell from './mroLab/MroLabShell';
import { useMroLabQueues } from './mroLab/useMroLabQueues';
import { LAB_LANE_LIST } from './mroLab/mroLabLanes';

function MroBoardLab() {
  const { loading, error, counts, refresh } = useMroLabQueues();
  const totalPending = LAB_LANE_LIST.reduce((sum, lane) => sum + (counts[lane.key] || 0), 0);

  return (
    <MroLabShell
      title="Overview"
      subtitle="Spectator redesign preview. Open a role lane to review its pending queue. The operational MRO Workbench is unchanged."
      accent="#37474f"
      counts={counts}
      actions={(
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={refresh}
          disabled={loading}
        >
          Refresh
        </Button>
      )}
    >
      <Stack spacing={2.5}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: '#fff'
          }}
        >
          <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                  All lanes
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.03em' }}>
                  {loading ? '—' : totalPending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Work packages currently waiting across Planning, Manager, Engineer, and Quality.
                </Typography>
              </Box>
              {loading ? <CircularProgress size={28} /> : null}
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2
          }}
        >
          {LAB_LANE_LIST.map((lane) => (
            <Card
              key={lane.key}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  borderColor: lane.accent,
                  boxShadow: `0 8px 24px ${lane.accentSoft}`
                }
              }}
            >
              <CardActionArea component={RouterLink} to={lane.path} sx={{ height: '100%' }}>
                <Box sx={{ height: 4, bgcolor: lane.accent }} />
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {lane.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {lane.subtitle}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: lane.accent, minWidth: 48, textAlign: 'right' }}
                      >
                        {loading ? '—' : counts[lane.key] ?? 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: lane.accent }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Open lane
                      </Typography>
                      <ArrowForward fontSize="small" />
                    </Stack>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Stack>
    </MroLabShell>
  );
}

export default MroBoardLab;
